import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { sortByTimestamp } from '@iotv/datamodel-core';
import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Tooltip } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { getDeviceMessages } from '../../../../data/NetworkSystem/DeviceMessage/getDeviceMessages';
import { getNextMessages } from '../../../../data/NetworkSystem/bi/getNextResults';
import { useMQTTChannelMessagesV2 } from '../../../../hooks/useMQTTChannelMessagesV2';
import { PeriodRecent, useModularTimePeriodSelector } from '../../../../hooks/useModularTimePeriodSelector';
import { ObjectViewType, Severity } from '../../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { DebouncedTextField } from '../../../common/DebouncedTextField';
import { SimpleIOModal } from '../../../io/VOBbased/SimpleIOModal';
import { IOType } from '../../../io/types';
import { LCDDeviceMessageTable } from '../LCDDeviceMessageTable';
import { columnMap } from '../config/columnMap';
import { getFlatLCDDeviceMessageForExport } from '../fns/getFlatLCDDeviceMessage';
import { useBasicColumVisibility } from '../hooks/useBasicColumnVisibility';
const { REACT_APP_PROJECT_CODE, REACT_APP_DEPLOYMENT_STAGE, REACT_APP_DEBUG } = process.env;
const projectCode = REACT_APP_PROJECT_CODE;
const stage = REACT_APP_DEPLOYMENT_STAGE;
const debug = REACT_APP_DEBUG && false;
const allowCreateSheets = stage === 'dev';
export var DisplayMode;
(function (DisplayMode) {
    DisplayMode["LIVE_DATA_ONLY"] = "LIVE_DATA_ONLY";
    DisplayMode["QUERY_ONLY"] = "QUERY_ONLY";
    DisplayMode["LIVE_AND_QUERY"] = "LIVE_AND_QUERY";
})(DisplayMode || (DisplayMode = {}));
export var SelectionFilter;
(function (SelectionFilter) {
    SelectionFilter["SELECTED_ITEMS"] = "SELECTED_ITEMS";
    SelectionFilter["ALL_ITEMS"] = "ALL_ITEMS";
})(SelectionFilter || (SelectionFilter = {}));
/** regex out of FlatLCDDeviceMessages: ^\s+(\S+):.*?\n   '$1',  ( improve with tab)  */
const headers = [
    'id',
    'timestamp',
    'timestampConverted',
    'propertyName',
    'customerName',
    'groupName',
    'nwID',
    'nasSourceIp',
    'app_id',
    'gatewayEui',
    'gatewayLat',
    'gatewayLng',
    'gatewayDOP',
    'deviceEui',
    'entityName',
    'devState',
    'deviceLat',
    'deviceLng',
    'devDoP',
    'battery',
    'batteryV',
    'batteryCharging',
    'temperature',
    'humidity',
    'consumedAirtimeSecs',
    'esp',
    'rssi',
    'snr',
    'sf',
    'power',
    'port',
    'frequency',
    'channel',
    'linkType',
    'fCnt',
    'fCntInc',
    'devFCntJumpDaily',
    'devMessageCntDaily',
    'ystrdayMsgCntJump',
    'ystrdayFCntJump',
    'size',
    'payloadStr',
    'adr',
    'bw',
    'codeRate',
    'deviceName',
    'dr',
    'deviceType',
    'gatewayIdName',
    'batteryErr',
    'downlinkAck',
    'distance',
    'linkCheckErr',
    'reboot',
    'deviceSk',
    'gatewaySk',
    'propertySk',
    'customerSk',
    'groupSk',
    'entitySk',
];
const useAthenaBI = true;
export const useNWDataTable = (props) => {
    const { selectedDevices, initialDisplayMode } = props;
    /** AppMainViewContext  */
    const [{ UserFunctions: userFunctions, contextUser: user, userSelectedGroup }] = useContext(AppMainViewContext);
    const [displayMode, setDisplayMode] = useState(initialDisplayMode ?? DisplayMode.LIVE_DATA_ONLY);
    const [liveDataFilter, setLiveDataFilter] = useState(selectedDevices.length === 0 ? SelectionFilter.ALL_ITEMS : SelectionFilter.SELECTED_ITEMS);
    const [querying, setQuerying] = useState(false);
    const [deviceMessages, setDeviceMessages] = useState([]);
    const [everyNth, setEveryNth] = useState(undefined);
    const selectedDevicesHash = selectedDevices.map((o) => o.id).join();
    const iterateGetDeviceMessages = async (params, iteration = 0) => {
        const { NextToken, QueryExecutionId } = params;
        let iterationData = null;
        let iterationErr = null;
        let iterationNextToken = undefined;
        let iterationQueryExecutionId = undefined;
        if (iteration === 0 && !NextToken) {
            setQuerying(true);
            const { err, data, NextToken, QueryExecutionId } = await getDeviceMessages(params);
            iterationData = data;
            iterationErr = err;
            iterationNextToken = NextToken;
            iterationQueryExecutionId = QueryExecutionId;
        }
        else if (NextToken && QueryExecutionId) {
            const { err, data, NextToken: nextNextToken, QueryExecutionId: nextQueryExecutionId, } = await getNextMessages({ QueryExecutionId, NextToken, transformType: 'deviceMessage' });
            iterationData = data;
            iterationErr = err;
            iterationNextToken = nextNextToken;
            iterationQueryExecutionId = nextQueryExecutionId; // should always be constant
        }
        if (iterationErr) {
            debug && console.log('useNWDataTable err in iterateGetDeviceMessages', iterationErr?.message);
            displayMessage(iterationErr.message, 'MessageBar', Severity.error);
        }
        if (iterationData) {
            if (iteration === 0)
                setDeviceMessages((dm) => [...iterationData].sort(sortByTimestamp).reverse());
            else
                setDeviceMessages((dm) => [...dm, ...iterationData].sort(sortByTimestamp).reverse());
        }
        if (iterationNextToken && iterationQueryExecutionId) {
            debug && console.log(`Getting next page with id ${QueryExecutionId} and next token ${iterationNextToken}`);
            iterateGetDeviceMessages({ ...params, NextToken: iterationNextToken, QueryExecutionId: iterationQueryExecutionId }, iteration + 1).finally(() => setQuerying(false));
        }
        else {
            setQuerying(false);
        }
    };
    /** Date range picker state vars */
    const { components: timeSelectorComponents, durationMode, fromDateValue, toDateValue, } = useModularTimePeriodSelector({
        rangeUnit: 'days',
        rangeLength: 1,
        initialPeriodRecent: PeriodRecent.RECENT,
        disabled: querying,
        requireConfirmation: true,
    });
    const externalColumnVisibility = Object.fromEntries(Object.keys(columnMap).map((k) => [k, true]));
    // state of columns being shown
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: 'networkDataTable',
        objectTypeId: 'networkData',
        viewType: ObjectViewType.List,
        externalColumnVisibility,
    });
    const { controls: ColumnVisibiltyControls } = useBasicColumVisibility({ setColumnVisibilityModel });
    const displayMessage = (content, type, severity, duration) => {
        const id = v4();
        userFunctions.setUserMessage({ id, content, label: content, type, severity: severity });
        setTimeout(() => userFunctions.dismissUserMessage({ id, content, label: content, type, severity: severity }), duration ?? 3000);
    };
    const addToDeviceMessages = (m) => {
        debug && console.log(`useNWDataTable child adding to device messages:`, m);
        setDeviceMessages((dm) => [m, ...dm].sort(sortByTimestamp).reverse());
    };
    /**
     * topicGetters are curried as useState will invoke the function on initial render ( without params ) https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
     * @returns
     */
    const networkDeviceTopicGetter = () => (xs) => {
        return xs.map((x) => `${projectCode}/${stage}/network/device/${x.eui ?? x.id}`);
    };
    const allNetworkDeviceTopicGetter = () => (xs) => [`${projectCode}/${stage}/network/device/#`];
    const [topicGetter, setTopicGetter] = React.useState(networkDeviceTopicGetter);
    const { setterGetters: [receivedObjects], } = useMQTTChannelMessagesV2({
        user,
        topicGetter,
        topicGenerationObjects: selectedDevices,
        addToParentItems: addToDeviceMessages,
    });
    useEffect(() => {
        debug && console.log('useNWDataTable used effect on:', liveDataFilter);
        if (liveDataFilter === SelectionFilter.ALL_ITEMS) {
            setTopicGetter(allNetworkDeviceTopicGetter);
        }
        else if (liveDataFilter === SelectionFilter.SELECTED_ITEMS) {
            setTopicGetter(networkDeviceTopicGetter);
        }
    }, [liveDataFilter]);
    useEffect(() => {
        debug && console.log('useNWDataTable use effect');
        if (displayMode !== DisplayMode.LIVE_DATA_ONLY) {
            if (selectedDevices.length > 0 || liveDataFilter === SelectionFilter.ALL_ITEMS) {
                const getNetworkData = async (selectedDeviceSks) => {
                    const params = {
                        userSelectedGroup,
                        deviceEuis: selectedDeviceSks.map((sk) => sk.replace('Device:', '')),
                        fromDateValue,
                        toDateValue,
                    };
                    await iterateGetDeviceMessages(params);
                };
                getNetworkData(selectedDevices.map((d) => d.sk));
            }
            else {
                console.log(`WARN: useNWDataTable not querying because:`, { selectedLength: selectedDevices.length, liveDataFilter });
            }
        }
        else {
            console.log(`useNWDataTable TEMPORARILY IN LIVE_DATA_ONLY`);
        }
    }, [fromDateValue.valueOf(), toDateValue.valueOf(), selectedDevicesHash, displayMode]);
    const handleModeChange = (e) => {
        const toggleMode = e.target.value;
        switch (displayMode) {
            case DisplayMode.LIVE_DATA_ONLY:
                if (toggleMode === DisplayMode.QUERY_ONLY)
                    setDisplayMode(DisplayMode.LIVE_AND_QUERY);
                return;
            case DisplayMode.LIVE_AND_QUERY:
                if (toggleMode === DisplayMode.LIVE_DATA_ONLY)
                    setDisplayMode(DisplayMode.QUERY_ONLY);
                if (toggleMode === DisplayMode.QUERY_ONLY)
                    setDisplayMode(DisplayMode.LIVE_DATA_ONLY);
                return;
            case DisplayMode.QUERY_ONLY:
                if (toggleMode === DisplayMode.LIVE_DATA_ONLY)
                    setDisplayMode(DisplayMode.LIVE_AND_QUERY);
                return;
        }
    };
    const handleLiveDataFilterChange = (e) => {
        debug && console.log('useNWDataTable setting liveDataFilter to:', e.target.value);
        const filter = e.target.value;
        setLiveDataFilter(filter);
        if (filter === SelectionFilter.ALL_ITEMS) {
            setDisplayMode(DisplayMode.LIVE_DATA_ONLY);
        }
    };
    return {
        Table: (_jsx(LCDDeviceMessageTable, { selectedDevices,
            deviceMessages: [...deviceMessages, ...receivedObjects],
            loading: querying,
            columnVisibilityModel,
            onColumnVisibilityModelChange,
            everyNth })),
        LiveDataControl: (_jsxs(FormGroup, { row: true, onChange: handleModeChange, children: [_jsx(FormControlLabel, { label: "Live", value: DisplayMode.LIVE_DATA_ONLY, control: _jsx(Checkbox, { checked: [DisplayMode.LIVE_DATA_ONLY, DisplayMode.LIVE_AND_QUERY].includes(displayMode) }) }, "live"), _jsx(Tooltip, { title: "Select devices first!", arrow: true, disableFocusListener: selectedDevices.length > 0, disableTouchListener: selectedDevices.length > 0, disableHoverListener: selectedDevices.length > 0, children: _jsx("span", { children: _jsx(FormControlLabel, { label: "Recent", value: DisplayMode.QUERY_ONLY, disabled: selectedDevices.length === 0, control: _jsx(Checkbox, { checked: [DisplayMode.QUERY_ONLY, DisplayMode.LIVE_AND_QUERY].includes(displayMode) }) }, "recent") }) })] }, 'liveDataControl')),
        SelectedOrAllControl: (_jsxs(RadioGroup, { value: liveDataFilter, onChange: handleLiveDataFilterChange, row: true, sx: { paddingInlineStart: 2 }, children: [_jsx(FormControlLabel, { label: "All", value: SelectionFilter.ALL_ITEMS, control: _jsx(Radio, { size: "small" }) }, "all"), _jsx(Tooltip, { title: "Select devices first!", arrow: true, disableFocusListener: selectedDevices.length > 0, disableTouchListener: selectedDevices.length > 0, disableHoverListener: selectedDevices.length > 0, children: _jsx("span", { children: _jsx(FormControlLabel, { label: "Selected", value: SelectionFilter.SELECTED_ITEMS, control: _jsx(Radio, { size: "small" }), disabled: selectedDevices.length === 0 }, "selected") }) })] })),
        TimePeriodSelector: _jsx("div", { children: timeSelectorComponents.TimePeriodSelector }), // Have to wrap in div or it crashes :/
        TimePeriodComponents: timeSelectorComponents,
        PeriodOrRecent: durationMode,
        EveryNth: (_jsx(DebouncedTextField, { value: everyNth, onChange: (e) => {
                if (e.target.value === '') {
                    setEveryNth(undefined);
                }
                const newNth = parseFloat(e.target.value);
                if (isNaN(newNth)) {
                    return;
                }
                setEveryNth(newNth);
            }, size: "small", label: "Every Nth" })),
        GoogleExport: (_jsx(SimpleIOModal, { allowIOTypeToggle: false,
            allowCreate: allowCreateSheets,
            initialIOType: IOType.EXPORT,
            columnIndexedKeys: headers,
            plainObjects: deviceMessages
                .filter((d, idx) => (everyNth ? everyNth > 1 && idx % everyNth === 0 : true))
                .map((m) => ({ type: 'DeviceMessage', ...getFlatLCDDeviceMessageForExport(m) })),
            setPlainObjectsInParent: () => { } }, 'GoogleExport')),
        ColumnVisibiltyControls,
        deviceMessages,
        displayMode,
        liveDataFilter,
    };
};
